import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const TermsContainer = styled.div`
  min-height: 100vh;
  background: #0a0a0a;
  color: #ffffff;
  padding: 2rem;
`;

const TermsContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 20px;
  padding: 3rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h1`
  color: #00ff88;
  text-align: center;
  margin-bottom: 1rem;
  font-size: 2.5rem;
`;

const LastUpdated = styled.p`
  text-align: center;
  color: #cccccc;
  margin-bottom: 2rem;
  font-style: italic;
`;

const Section = styled.section`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  color: #00ff88;
  margin-bottom: 1rem;
  font-size: 1.5rem;
`;

const Paragraph = styled.p`
  margin-bottom: 1rem;
  line-height: 1.6;
  color: #cccccc;
`;

const List = styled.ul`
  margin-bottom: 1rem;
  padding-left: 2rem;
  color: #cccccc;
`;

const ListItem = styled.li`
  margin-bottom: 0.5rem;
  line-height: 1.6;
`;

const Strong = styled.strong`
  color: #ffffff;
`;

const LinkStyled = styled(Link)`
  color: #00ff88;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ContactInfo = styled.div`
  background: rgba(0, 255, 136, 0.1);
  border: 1px solid #00ff88;
  border-radius: 10px;
  padding: 1.5rem;
  margin: 2rem 0;
`;

const TermsOfService = () => {
  return (
    <TermsContainer>
      <TermsContent>
        <Title>Terms of Service</Title>
        <LastUpdated>Last updated: July 8, 2025</LastUpdated>

        <Section>
          <Paragraph>
            Welcome to Skylink ("we," "us," or "our"). These Terms of Service ("Terms") govern your use of our 
            voice-messaging, SMS, and IVR services that facilitate communication between incarcerated individuals 
            and their friends and family.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>1. Acceptance of Terms</SectionTitle>
          <Paragraph>
            By accessing or using our services, you agree to be bound by these Terms. If you do not agree to these 
            Terms, please do not use our services.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>2. Service Description</SectionTitle>
          <Paragraph>
            Skylink provides communication services including:
          </Paragraph>
          <List>
            <ListItem>Voice messaging and call recording services</ListItem>
            <ListItem>SMS text messaging services</ListItem>
            <ListItem>Interactive Voice Response (IVR) systems</ListItem>
            <ListItem>Account management and payment processing</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>3. SMS and Voice Messaging Consent</SectionTitle>
          <Paragraph>
            <Strong>By providing your phone number and checking the consent box, you expressly consent to receive:</Strong>
          </Paragraph>
          <List>
            <ListItem>Transactional SMS messages related to your account</ListItem>
            <ListItem>Service notifications and updates</ListItem>
            <ListItem>Security alerts and account verification messages</ListItem>
            <ListItem>Marketing messages about our services (if you opt-in)</ListItem>
          </List>
          
                     <Paragraph>
             <Strong>Message frequency:</Strong> Message frequency will vary based on your account activity and service usage.
           </Paragraph>
          
          <Paragraph>
            <Strong>Message and data rates may apply.</Strong> Your carrier's standard messaging rates apply to SMS messages.
          </Paragraph>
          
          <Paragraph>
            <Strong>Opt-out instructions:</Strong> You may opt out of SMS messages at any time by replying "STOP" to any message. 
            You may also opt out by contacting us at <a href="mailto:connectinoutinc@gmail.com">connectinoutinc@gmail.com</a>.
          </Paragraph>
          
          <Paragraph>
            <Strong>Help:</Strong> Reply "HELP" to any message for assistance.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>4. User Responsibilities</SectionTitle>
          <Paragraph>You agree to:</Paragraph>
          <List>
            <ListItem>Provide accurate and complete information</ListItem>
            <ListItem>Maintain the security of your account credentials</ListItem>
            <ListItem>Use the service only for lawful purposes</ListItem>
            <ListItem>Not interfere with or disrupt the service</ListItem>
            <ListItem>Comply with all applicable laws and regulations</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>5. Prohibited Uses</SectionTitle>
          <Paragraph>You may not use our services to:</Paragraph>
          <List>
            <ListItem>Send spam, unsolicited messages, or harassing communications</ListItem>
            <ListItem>Impersonate another person or entity</ListItem>
            <ListItem>Transmit illegal, harmful, or objectionable content</ListItem>
            <ListItem>Attempt to gain unauthorized access to our systems</ListItem>
            <ListItem>Violate any applicable laws or regulations</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>6. Privacy and Data Protection</SectionTitle>
          <Paragraph>
            Your privacy is important to us. Please review our <LinkStyled to="/privacy-policy">Privacy Policy</LinkStyled>, 
            which explains how we collect, use, and protect your information.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>7. Payment Terms</SectionTitle>
          <Paragraph>
            Some services require payment. All fees are non-refundable unless otherwise stated. We reserve the right 
            to change our pricing with 30 days' notice.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>8. Service Availability</SectionTitle>
          <Paragraph>
            We strive to provide reliable service but cannot guarantee uninterrupted availability. We may temporarily 
            suspend service for maintenance or other reasons.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>9. Limitation of Liability</SectionTitle>
          <Paragraph>
            To the maximum extent permitted by law, Skylink shall not be liable for any indirect, incidental, 
            special, consequential, or punitive damages arising from your use of our services.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>10. Indemnification</SectionTitle>
          <Paragraph>
            You agree to indemnify and hold harmless Skylink from any claims arising from your use of our services 
            or violation of these Terms.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>11. Termination</SectionTitle>
          <Paragraph>
            We may terminate or suspend your account at any time for violation of these Terms. You may terminate 
            your account by contacting us.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>12. Changes to Terms</SectionTitle>
          <Paragraph>
            We may update these Terms from time to time. We will notify you of material changes via email or 
            through our website.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>13. Governing Law</SectionTitle>
          <Paragraph>
            These Terms are governed by the laws of the Commonwealth of Massachusetts, without regard to 
            conflict of law principles.
          </Paragraph>
        </Section>

        <ContactInfo>
          <SectionTitle>Contact Us</SectionTitle>
          <Paragraph>
            If you have questions about these Terms of Service, please contact us:
          </Paragraph>
          <Paragraph>
            <Strong>Skylink, Inc.</Strong><br />
            Email: <a href="mailto:connectinoutinc@gmail.com">connectinoutinc@gmail.com</a><br />
            Address: PO Box 35437, Boston, MA 02135
          </Paragraph>
        </ContactInfo>
      </TermsContent>
    </TermsContainer>
  );
};

export default TermsOfService;
